/**
 * /sudo/api-usage — per-call + per (tenant x day) rollup of paid
 * external API spend.
 *
 * Read-only. Metadata only — never shows prompt text or response
 * content. Cost figures are USD micro-dollars at rest, converted to
 * AUD on the client via the rate returned by the API (Frankfurter,
 * cached 24h).
 */
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useDocumentTitle } from '../lib/use-document-title';

export const Route = createFileRoute('/sudo/api-usage')({
  component: ApiUsagePage,
});

type Provider = 'cloudflare_ai' | 'mapbox' | 'twilio' | 'cloudflare_email';

interface RecentRow {
  id: string;
  tenantId: string | null;
  tenantName: string | null;
  provider: Provider;
  model: string;
  units: number;
  unitsKind: string;
  costUsdMicro: number;
  createdAt: number;
}

interface RollupRow {
  tenantId: string | null;
  tenantName: string | null;
  day: string;
  totalCostUsdMicro: number;
  callCount: number;
  byProvider: Partial<Record<Provider, number>>;
}

interface ApiUsageResponse {
  rate: { usdToAud: number; asOf: string };
  days: number;
  recent: RecentRow[];
  rollup: RollupRow[];
}

async function fetchApiUsage(days: number): Promise<ApiUsageResponse> {
  const r = await fetch(`/api/super/api-usage?days=${days}`);
  if (!r.ok) throw new Error('api_usage_failed');
  return r.json();
}

function ApiUsagePage() {
  useDocumentTitle('API usage — Super-admin');
  const q = useQuery({
    queryKey: ['sudo', 'api-usage', 30],
    queryFn: () => fetchApiUsage(30),
  });

  return (
    <div className="space-y-10">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">API usage</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Every paid external API hit, the last 30 days. Metadata only — no
          prompt text or response content is logged.
        </p>
      </header>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading&hellip;</p>
      ) : q.data ? (
        <>
          <FxBanner rate={q.data.rate} />
          <RollupSection rows={q.data.rollup} rate={q.data.rate.usdToAud} />
          <RecentSection rows={q.data.recent} rate={q.data.rate.usdToAud} />
        </>
      ) : (
        <p className="text-sm text-destructive">Failed to load API usage.</p>
      )}
    </div>
  );
}

function FxBanner({ rate }: { rate: { usdToAud: number; asOf: string } }) {
  return (
    <div className="rounded-2xl border border-brand-sage/15 bg-card p-4 text-sm shadow-brand">
      <span className="font-medium">
        USD &rarr; AUD: {rate.usdToAud.toFixed(4)}
      </span>{' '}
      <span className="text-muted-foreground">
        Source: <a href="https://frankfurter.dev" target="_blank" rel="noopener noreferrer" className="underline">frankfurter.dev</a>
        {rate.asOf === 'fallback' ? ' (fallback rate)' : ` (as of ${rate.asOf})`}
      </span>
    </div>
  );
}

function RollupSection({
  rows,
  rate,
}: {
  rows: RollupRow[];
  rate: number;
}) {
  return (
    <section>
      <div className="flex items-baseline justify-between">
        <h2 className="text-xl font-semibold tracking-tight">
          Per (business &times; day)
        </h2>
        <span className="text-xs text-muted-foreground">
          {rows.length} bucket{rows.length === 1 ? '' : 's'}
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">
          No API spend recorded yet.
        </p>
      ) : (
        <div className="mt-6 overflow-hidden rounded-2xl border border-brand-sage/15 bg-card shadow-brand">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="border-b border-brand-sage/10 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Day</th>
                  <th className="px-4 py-3">Business</th>
                  <th className="px-4 py-3 text-right">Calls</th>
                  <th className="px-4 py-3 text-right">USD</th>
                  <th className="px-4 py-3 text-right">AUD</th>
                  <th className="px-4 py-3">By provider (USD)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={`${r.tenantId ?? '_'}|${r.day}`}
                    className="border-b border-brand-sage/10 last:border-b-0 hover:bg-secondary/40"
                  >
                    <td className="px-4 py-3 tabular-nums">{r.day}</td>
                    <td className="px-4 py-3">
                      {r.tenantName ?? (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {r.callCount}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {fmtUsd(r.totalCostUsdMicro)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium">
                      {fmtAud(r.totalCostUsdMicro, rate)}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {Object.entries(r.byProvider)
                        .filter(([, v]) => v && v > 0)
                        .map(([p, v]) => `${p}: ${fmtUsd(v ?? 0)}`)
                        .join(' · ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

function RecentSection({
  rows,
  rate,
}: {
  rows: RecentRow[];
  rate: number;
}) {
  return (
    <section>
      <div className="flex items-baseline justify-between">
        <h2 className="text-xl font-semibold tracking-tight">
          Per-call (last 200)
        </h2>
      </div>

      {rows.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">
          No calls logged in this window.
        </p>
      ) : (
        <div className="mt-6 overflow-hidden rounded-2xl border border-brand-sage/15 bg-card shadow-brand">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[840px] text-left text-sm">
              <thead className="border-b border-brand-sage/10 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Time</th>
                  <th className="px-4 py-3">Business</th>
                  <th className="px-4 py-3">Provider</th>
                  <th className="px-4 py-3">Model</th>
                  <th className="px-4 py-3 text-right">Units</th>
                  <th className="px-4 py-3 text-right">USD</th>
                  <th className="px-4 py-3 text-right">AUD</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-brand-sage/10 last:border-b-0 hover:bg-secondary/40"
                  >
                    <td className="px-4 py-3 text-xs text-muted-foreground tabular-nums">
                      {new Date(r.createdAt).toLocaleString('en-AU', {
                        hour12: false,
                      })}
                    </td>
                    <td className="px-4 py-3">
                      {r.tenantName ?? (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">{r.provider}</td>
                    <td className="px-4 py-3 text-xs font-mono">{r.model}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {r.units} {r.unitsKind}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {fmtUsd(r.costUsdMicro)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {fmtAud(r.costUsdMicro, rate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

function fmtUsd(microUsd: number): string {
  const usd = microUsd / 1_000_000;
  if (usd === 0) return '$0';
  if (usd < 0.01) return `$${usd.toFixed(6)}`;
  return `$${usd.toFixed(4)}`;
}

function fmtAud(microUsd: number, usdToAud: number): string {
  const aud = (microUsd / 1_000_000) * usdToAud;
  if (aud === 0) return 'A$0';
  if (aud < 0.01) return `A$${aud.toFixed(6)}`;
  return `A$${aud.toFixed(4)}`;
}
